import * as React from 'react'
import { createPortal } from 'react-dom'
import { styled } from 'next-yak'

import { t } from '@owl-nest/localize'
import * as plume from '@ulule/owl-kit-components/next'
import * as services from '@owl-nest/services'
import * as hooks from '@owl-nest/hooks'
import * as messages from '@owl-nest/messages'

import { CommentForm } from './CommentBox/CommentForm'
import { CommentList } from './CommentBox/CommentList'

type CommentBox = {
  objectId: number | string
  isCommentingAllowed?: boolean
}

const Connect = React.lazy(async () => ({ default: (await import('@owl-nest/connect')).Connect }))

const PAGINATION_LIMIT = 10

export function CommentBox({ objectId, isCommentingAllowed = false }: CommentBox): React.ReactElement<any> {
  const isClient = typeof window !== 'undefined'
  const auth = services.user.useAuth()
  const { createComment, response, queryNext, queryReplies, replyToComment, deleteComment } =
    services.comment.useNewsComments(objectId, {
      fetchOnMount: true,
      params: {
        limit: PAGINATION_LIMIT,
        since: Date.now(),
      },
    })
  const messenger = messages.useMessage()

  const [isModalOpen, setIsModalOpen] = hooks.useModal('ConfirmDeleteComment')
  const commentIdToDelete = hooks.useModalParams('ConfirmDeleteComment')?.comment.id

  const comments = response.data?.comments
  const meta = response.data?.meta

  const modalContainerCached = React.useMemo(() => {
    if (!isClient) {
      return null
    }

    return document.getElementById('modal-container') ?? document.body
  }, [isClient])

  const modalContainer = modalContainerCached ?? (isClient ? document.body : null)

  if (!comments || !meta) {
    return <plume.Spinner />
  }

  return (
    <>
      {auth.type === 'loggedin' ? (
        <>
          {isCommentingAllowed && (
            <CommentForm
              currentUser={auth.user}
              onSubmit={async (values) => {
                await createComment({
                  comment: values.comment,
                })
              }}
              submitLabel={t('Send')}
            />
          )}
        </>
      ) : (
        <React.Suspense fallback={<plume.Spinner />}>
          <ConnectWrapper>
            <plume.styles.heading.M>{t('Login to leave a comment')}</plume.styles.heading.M>
            <Connect extraFields={[]} />
          </ConnectWrapper>
        </React.Suspense>
      )}

      {comments && (
        <CommentList
          comments={comments}
          meta={meta}
          objectId={objectId}
          queryNext={queryNext}
          queryReplies={queryReplies}
          replyToComment={replyToComment}
        />
      )}

      {createPortal(
        <plume.Modal
          open={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          closable
          buttons={[
            {
              type: 'button',
              props: {
                kind: 'primary',
                color: 'red',
                onClick: async () => {
                  if (!commentIdToDelete) {
                    return
                  }

                  await deleteComment(commentIdToDelete).then(
                    () => {
                      messenger.confirm({
                        context: 'delete-comment',
                        forceClose: true,
                        message: t('The comment has been deleted.'),
                        type: 'toast',
                      })
                    },
                    () => {
                      messenger.error({
                        context: 'delete-comment',
                        message: t('Failed to delete comment, try again'),
                        type: 'toast',
                      })
                    },
                  )

                  setIsModalOpen(false)
                },
                children: t('Yes, delete'),
              },
            },
            {
              type: 'linkAsButton',
              props: {
                kind: 'secondary',
                onClick: () => setIsModalOpen(false),
                children: t('No, keep'),
              },
            },
          ]}
        >
          <ModalContent>
            <plume.styles.heading.S>{t('Delete this comment?')}</plume.styles.heading.S>
            <plume.styles.copy.M.light>
              {t('The comment will be deleted for everyone. This action is irreversible.')}
            </plume.styles.copy.M.light>
          </ModalContent>
        </plume.Modal>,
        modalContainer,
      )}
    </>
  )
}

const ModalContent = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  max-width: 400px;
  text-align: center;
  gap: 8px;
  margin: 0 auto;
`

const ConnectWrapper = styled.div`
  max-width: 430px;
  margin: 0 auto 30px;

  ${plume.styles.heading.M} {
    text-align: center;
    margin-bottom: 30px;
  }
`
